import { useState } from 'react'
import { ChevronDown, ChevronRight, MessageSquare } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useChatbotTestRuns, useChatbotTestRunDetail, type ChatbotTestRun } from '@/hooks/useSimulations'
import { ConversationViewer } from './ConversationViewer'

const statusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'secondary',
  running: 'default',
  completed: 'outline',
  failed: 'destructive',
  passed: 'outline',
  error: 'destructive',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={statusVariant[status] ?? 'secondary'} className="text-xs">
      {status}
    </Badge>
  )
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

function RunResultsRow({ testId, run, onShowChat }: { testId: number; run: ChatbotTestRun; onShowChat: (conversationId: string) => void }) {
  const { data: detail } = useChatbotTestRunDetail(testId, run.id)

  if (!detail?.results?.length) {
    return (
      <TableRow>
        <TableCell colSpan={5} className="text-center text-xs text-gray-400 py-2">
          No results yet
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      {detail.results.map((result) => (
        <TableRow key={result.id} className="bg-gray-50">
          <TableCell className="pl-10 text-xs text-gray-500">#{result.id}</TableCell>
          <TableCell><StatusBadge status={result.status} /></TableCell>
          <TableCell className="text-xs text-gray-600 whitespace-pre-wrap break-words">
            {result.evaluation || result.error || '-'}
          </TableCell>
          <TableCell>
            {result.conversation_id ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs gap-1"
                onClick={(e) => {
                  e.stopPropagation()
                  onShowChat(result.conversation_id!)
                }}
              >
                <MessageSquare className="w-3 h-3" />
                Show Chat
              </Button>
            ) : (
              <span className="text-xs text-gray-500">-</span>
            )}
          </TableCell>
          <TableCell className="text-xs text-gray-500">{formatDate(result.completed_at)}</TableCell>
        </TableRow>
      ))}
    </>
  )
}

interface RunHistoryProps {
  testId: number
}

export function RunHistory({ testId }: RunHistoryProps) {
  const { data: runs = [], isLoading } = useChatbotTestRuns(testId)
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null)
  const [viewingConversationId, setViewingConversationId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  if (runs.length === 0) {
    return <div className="text-center py-4 text-gray-500 text-xs">No runs yet</div>
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8"></TableHead>
            <TableHead className="text-xs">Status</TableHead>
            <TableHead className="text-xs">Started</TableHead>
            <TableHead className="text-xs">Completed</TableHead>
            <TableHead className="text-xs">Summary</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => {
            const isExpanded = expandedRunId === run.id
            return (
              <>
                <TableRow
                  key={run.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                >
                  <TableCell className="w-8">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </TableCell>
                  <TableCell><StatusBadge status={run.status} /></TableCell>
                  <TableCell className="text-xs text-gray-600">{formatDate(run.started_at)}</TableCell>
                  <TableCell className="text-xs text-gray-600">{formatDate(run.completed_at)}</TableCell>
                  <TableCell className="text-xs text-gray-600">
                    {run.summary
                      ? `${run.summary.passed}/${run.summary.total} passed`
                      : '-'}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <RunResultsRow
                    testId={testId}
                    run={run}
                    onShowChat={setViewingConversationId}
                  />
                )}
              </>
            )
          })}
        </TableBody>
      </Table>

      {viewingConversationId && (
        <ConversationViewer
          testId={testId}
          conversationId={viewingConversationId}
          onClose={() => setViewingConversationId(null)}
        />
      )}
    </>
  )
}
